import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Plus, X, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { uid } from "../lib/uid";
import { Layout } from "../components/Layout";
import { OriginalityScan } from "../components/OriginalityScan";
import { useAuth } from "../context/AuthContext";

const AdminNew = () => {
  const { user, ready } = useAuth();
  const nav = useNavigate();

  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({
    title: "",
    hook: "",
    full_fact: "",
    subject_slug: "",
    image_url: "",
  });
  const [tagsInput, setTagsInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [related, setRelated] = useState([]);
  const [sources, setSources] = useState([
    { id: uid(), label: "", url: "" },
    { id: uid(), label: "", url: "" },
  ]);
  const [checks, setChecks] = useState({ ownWords: false, sources: false, image: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ready && !user) nav("/admin", { replace: true });
  }, [ready, user, nav]);

  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data));
  }, []);

  const tags = useMemo(
    () => tagsInput.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
    [tagsInput]
  );

  // Debounced related suggestions from shared tags.
  useEffect(() => {
    if (tags.length === 0) {
      setSuggestions([]);
      return;
    }
    const id = setTimeout(() => {
      api
        .get("/admin/suggest-related", { params: { tags: tags.join(",") } })
        .then((r) => setSuggestions(r.data))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(id);
  }, [tagsInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const socialCaption = useMemo(() => {
    if (!form.hook && !form.title) return "";
    return `${form.hook} — "${form.title}" | Explore more curios at CurioVault.co`;
  }, [form.hook, form.title]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleRelated = (slug) =>
    setRelated((r) => (r.includes(slug) ? r.filter((s) => s !== slug) : [...r, slug]));

  const validSources = useMemo(
    () => sources.filter((s) => s.url.trim()),
    [sources]
  );

  const setSourceField = (i, field) => (e) =>
    setSources((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: e.target.value } : r)));
  const addSourceRow = () => setSources((rows) => [...rows, { id: uid(), label: "", url: "" }]);
  const removeSourceRow = (i) => setSources((rows) => rows.filter((_, idx) => idx !== i));

  const allChecked = checks.ownWords && checks.sources && checks.image;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject_slug) {
      toast.error("Please choose a subject.");
      return;
    }
    if (validSources.length < 2) {
      toast.error("Please list at least 2 sources.");
      return;
    }
    if (!allChecked) {
      toast.error("Please complete the copyright compliance checklist.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/facts", {
        ...form,
        tags,
        related,
        sources: validSources,
        compliance: { ownWords: checks.ownWords, sources: checks.sources, image: checks.image },
      });
      toast.success("Curio catalogued!");
      nav(`/curio/${res.data.slug}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save the curio.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full font-ui text-sm px-3 py-2.5 bg-[#FAF7F0] border border-[#D8CEB9] rounded-sm outline-none focus:border-[#9E4535] focus:ring-2 focus:ring-[#F5E8EA]";

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <p className="caption-label mb-1">Curator Desk</p>
        <h1 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#1A1615] mb-8">Catalogue a New Curio</h1>

        <form onSubmit={submit} className="space-y-6">
          <div className="bg-white border border-[#D8CEB9] rounded-sm p-6 space-y-5">
            <div>
              <label className="caption-label block mb-1.5">Title</label>
              <input data-testid="fact-title-input" value={form.title} onChange={set("title")} required placeholder="The Creature With Blue Blood…" className={inputCls} />
              {form.title && <p className="font-mono-cv text-[11px] text-[#7A6C5D] mt-1.5">URL: /curio/{form.title.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/[\s_-]+/g, "-")}</p>}
            </div>
            <div>
              <label className="caption-label block mb-1.5">Short Hook <span className="normal-case tracking-normal text-[#B8AC9C]">(one sentence, for social media)</span></label>
              <input data-testid="fact-hook-input" value={form.hook} onChange={set("hook")} required placeholder="A single tantalising sentence." className={inputCls} />
            </div>
            <div>
              <label className="caption-label block mb-1.5">Full Curio <span className="normal-case tracking-normal text-[#B8AC9C]">(150–250 words, blank line between paragraphs)</span></label>
              <textarea data-testid="fact-body-input" value={form.full_fact} onChange={set("full_fact")} required rows={9} className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="caption-label block mb-1.5">Subject</label>
                <select data-testid="fact-subject-select" value={form.subject_slug} onChange={set("subject_slug")} required className={inputCls}>
                  <option value="">Choose a subject…</option>
                  {subjects.map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="caption-label block mb-1.5">Tags <span className="normal-case tracking-normal text-[#B8AC9C]">(comma separated)</span></label>
                <input data-testid="fact-tags-input" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="Victorian, venomous, space" className={inputCls} />
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {tags.map((t) => (
                      <span key={t} className="font-ui text-[11px] px-2 py-0.5 rounded-full bg-[#F5E8EA] text-[#9E4535]">#{t}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="caption-label block mb-1.5">Hero Image URL</label>
              <input data-testid="fact-image-input" value={form.image_url} onChange={set("image_url")} placeholder="https://…" className={inputCls} />
            </div>
            {form.image_url && (
              <div className="w-full aspect-[3/2] max-w-xs overflow-hidden rounded-sm border border-[#D8CEB9] bg-[#F3EDE0]">
                <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          {/* Sources & Further Reading */}
          <div className="bg-white border border-[#D8CEB9] rounded-sm p-6" data-testid="sources-editor">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-serif-display text-lg font-semibold text-[#1A1615]">Sources &amp; Further Reading</h2>
              <span className="font-mono-cv text-[11px] text-[#7A6C5D]">{validSources.length} listed · min 2</span>
            </div>
            <p className="font-body text-xs text-[#7A6C5D] mb-4">List the sources you researched. These appear on the curio page for verification.</p>
            <div className="space-y-3">
              {sources.map((s, i) => (
                <div key={s.id} className="flex gap-2 items-start" data-testid={`source-row-${i}`}>
                  <input
                    value={s.label}
                    onChange={setSourceField(i, "label")}
                    placeholder="Label (e.g. Wikipedia)"
                    data-testid={`source-label-${i}`}
                    className={`${inputCls} sm:max-w-[38%]`}
                  />
                  <input
                    value={s.url}
                    onChange={setSourceField(i, "url")}
                    placeholder="https://…"
                    data-testid={`source-url-${i}`}
                    className={inputCls}
                  />
                  {sources.length > 1 && (
                    <button type="button" onClick={() => removeSourceRow(i)} data-testid={`remove-source-${i}`} className="mt-1 text-[#7A6C5D] hover:text-[#9E4535]" aria-label="Remove source">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addSourceRow} data-testid="add-source-button" className="mt-3 inline-flex items-center gap-1.5 font-ui text-xs font-semibold text-[#9E4535] hover:underline">
              <Plus className="w-3.5 h-3.5" /> Add another source
            </button>
          </div>

          {/* Originality scan */}
          <OriginalityScan fullFact={form.full_fact} sources={validSources} />

          {/* Auto social caption (hidden field preview) */}
          {socialCaption && (
            <div className="bg-[#FDF8ED] border border-[#E4D3A8] rounded-sm p-5" data-testid="social-caption-preview">
              <p className="caption-label !text-[#B08A42] mb-2">Auto-generated Social Caption</p>
              <p className="font-body text-sm text-[#4A4036] italic">{socialCaption}</p>
              <p className="font-mono-cv text-[10px] text-[#B8AC9C] mt-2">Stored on the curio for future auto-posting.</p>
            </div>
          )}

          {/* Suggested related facts */}
          <div className="bg-white border border-[#D8CEB9] rounded-sm p-6">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-[#C5A059]" />
              <h2 className="font-serif-display text-lg font-semibold text-[#1A1615]">Suggested Connections</h2>
            </div>
            <p className="font-body text-xs text-[#7A6C5D] mb-4">Ranked by shared tags. Tick any to link as a Connected Curiosity.</p>
            {suggestions.length > 0 ? (
              <div className="space-y-2" data-testid="related-suggestions">
                {suggestions.map((s) => (
                  <label
                    key={s.slug}
                    data-testid={`suggestion-${s.slug}`}
                    className={`flex items-center gap-3 p-2.5 rounded-sm border cursor-pointer transition-colors ${
                      related.includes(s.slug) ? "border-[#9E4535] bg-[#F5E8EA]" : "border-[#D8CEB9] hover:border-[#B8AC9C]"
                    }`}
                  >
                    <input type="checkbox" checked={related.includes(s.slug)} onChange={() => toggleRelated(s.slug)} className="accent-[#9E4535] w-4 h-4" />
                    <div className="w-12 h-12 shrink-0 overflow-hidden rounded-sm bg-[#F3EDE0]">
                      <img src={s.image_url} alt="" className="w-full h-full object-cover sepia-[0.15]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-serif-display text-sm font-semibold text-[#1A1615] truncate">{s.title}</p>
                      <p className="font-mono-cv text-[10px] text-[#7A6C5D]">{s.shared_count} shared tag{s.shared_count !== 1 ? "s" : ""}</p>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <p className="font-body text-sm text-[#B8AC9C]">Add tags above to see suggestions.</p>
            )}
          </div>

          {/* Copyright compliance checklist */}
          <div className="bg-[#F5E8EA] border border-[#E4C9CD] rounded-sm p-6" data-testid="compliance-checklist">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-4 h-4 text-[#9E4535]" />
              <h2 className="font-serif-display text-lg font-semibold text-[#1A1615]">Copyright Compliance</h2>
            </div>
            <div className="space-y-3 font-body text-sm text-[#4A4036]">
              <label className="flex gap-3 items-start cursor-pointer" data-testid="check-own-words-label">
                <input type="checkbox" checked={checks.ownWords} onChange={(e) => setChecks((c) => ({ ...c, ownWords: e.target.checked }))} data-testid="check-own-words" className="accent-[#9E4535] w-4 h-4 mt-0.5" />
                <span>I have written this in my <strong>OWN words</strong>; I have not copied more than 10 words in a row from any source.</span>
              </label>
              <label className="flex gap-3 items-start cursor-pointer" data-testid="check-sources-label">
                <input type="checkbox" checked={checks.sources} onChange={(e) => setChecks((c) => ({ ...c, sources: e.target.checked }))} data-testid="check-sources" className="accent-[#9E4535] w-4 h-4 mt-0.5" />
                <span>I have listed <strong>at least 2 sources</strong>.</span>
              </label>
              <label className="flex gap-3 items-start cursor-pointer" data-testid="check-image-label">
                <input type="checkbox" checked={checks.image} onChange={(e) => setChecks((c) => ({ ...c, image: e.target.checked }))} data-testid="check-image" className="accent-[#9E4535] w-4 h-4 mt-0.5" />
                <span>Image is <strong>copyright-free</strong> (I own it, or it is from Unsplash/Pexels/Wikimedia Commons with the license checked).</span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving || !allChecked || validSources.length < 2}
            data-testid="save-fact-button"
            className="inline-flex items-center gap-2 font-ui text-sm font-semibold px-6 py-3 bg-[#9E4535] text-[#FAF7F0] rounded-sm hover:bg-[#82382B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" /> {saving ? "Saving…" : "Add to the Vault"}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default AdminNew;
